import React, { useMemo, useState, useCallback } from 'react';
import propTypes from 'prop-types';
import { USAMap } from '@mirawision/usa-map-react';
import { getStateNameFromStateCode } from 'helpers/states';
import useChangedStatePulse from 'hooks/useChangedStatePulse';
import { NO_CARRIERS_MATCH_FILTERS_MESSAGE } from 'helpers/carriers/carriers';
import FilterAutocomplete from 'tailwind-components/FilterAutocomplete/FilterAutocomplete';

const SBE_FILL = '#99d7f9';
const FFM_FILL = '#0c79cc';
const LIME = '#d5ff8d';
// states with no carriers are greyed out so it is easy to see where products exist
const EMPTY_FILL = '#d1d5db';
const EMPTY_HOVER_FILL = '#b8bec7';
const EMPTY_LABEL = '#6b7280';

// share of lime blended into a state's own color while it pulses; full lime is too jarring on dark blue
const PULSE_LIME_MIX = 0.4;

// blends two #rrggbb colors; `amount` is how much of `to` ends up in the result (0-1)
function mixHexColors(from, to, amount) {
  const channels = hex =>
    [1, 3, 5].map(start => parseInt(hex.slice(start, start + 2), 16));
  const toHex = value => Math.round(value).toString(16).padStart(2, '0');
  const [fromChannels, toChannels] = [channels(from), channels(to)];
  return `#${fromChannels
    .map((value, i) => toHex(value + (toChannels[i] - value) * amount))
    .join('')}`;
}

/**
 * usa map colored by SBE/FFM with per-state carrier tooltips and an optional carrier-type filter
 * @param carrierPartnersMap - `{ [stateCode]: { SBE, carriers } }` for every state
 * @param onStateClick - called with the clicked state code
 * @param selectedStateCode - state to keep highlighted
 * @param carrierTypeOptions - `[{ value, label }]` carrier types; the filter shows with 2+
 * @param selectedCarrierTypes - selected carrier type values (empty means all)
 * @param onCarrierTypesChange - called with the next selected carrier type values
 */
export default function CarrierAppointmentsByStateMap({
  carrierPartnersMap = {},
  onStateClick,
  selectedStateCode = null,
  carrierTypeOptions = [],
  selectedCarrierTypes = [],
  onCarrierTypesChange
}) {
  const [hoveredState, setHoveredState] = useState(null);
  const [hoverGroup, setHoverGroup] = useState(null);
  const showTypeFilter =
    Boolean(onCarrierTypesChange) && carrierTypeOptions.length > 1;
  const hasActiveFilters = selectedCarrierTypes.length > 0;
  const pulsingStates = useChangedStatePulse({
    stateMap: carrierPartnersMap,
    filterKey: selectedCarrierTypes.join('|'),
    paused: hoverGroup !== null
  });
  const hasEmptyStates = Object.values(carrierPartnersMap).some(
    state => !state.carriers?.length
  );
  const selectedTypeOptions = carrierTypeOptions.filter(o =>
    selectedCarrierTypes.includes(o.value)
  );

  const handleStateClick = useCallback(
    code => {
      onStateClick?.(code);
    },
    [onStateClick]
  );

  const customStates = useMemo(() => {
    const result = {};
    Object.keys(carrierPartnersMap).forEach(code => {
      const state = carrierPartnersMap[code];
      const hasCarriers = state.carriers?.length > 0;
      const groupFill = state.SBE ? SBE_FILL : FFM_FILL;
      const baseFill = hasCarriers ? groupFill : EMPTY_FILL;

      let fill = baseFill;
      if (hoverGroup === 'SBE' || hoverGroup === 'FFM') {
        const inHoveredGroup =
          (hoverGroup === 'SBE' && state.SBE) ||
          (hoverGroup === 'FFM' && !state.SBE);
        // states without carriers stay grey so the legend only lights up where products exist
        if (inHoveredGroup && hasCarriers) {
          fill = LIME;
        }
      } else {
        const highlight = hoveredState === code || selectedStateCode === code;
        if (highlight && hasCarriers) {
          fill = LIME;
        } else if (highlight) {
          fill = EMPTY_HOVER_FILL;
        } else if (pulsingStates.has(code)) {
          fill = mixHexColors(baseFill, LIME, PULSE_LIME_MIX);
        }
      }

      let labelColor = '#ffffff';
      if (!hasCarriers) {
        labelColor = EMPTY_LABEL;
      } else if (hoveredState === code) {
        labelColor = '#0c79cc';
      }

      result[code] = {
        fill,
        stroke: '#fff',
        label: { enabled: true, fill: labelColor },
        tooltip: {
          enabled: true,
          render: () => (
            <div style={{ maxWidth: '200px' }}>
              <strong>{getStateNameFromStateCode(code)}</strong>
              <br />
              <br />
              {state.carriers?.length > 0 ? (
                <span>
                  <strong>Available carriers:</strong>{' '}
                  {state.carriers.join(', ')}
                </span>
              ) : (
                <span>
                  {hasActiveFilters
                    ? NO_CARRIERS_MATCH_FILTERS_MESSAGE
                    : 'No carriers found for this state.'}
                </span>
              )}
            </div>
          )
        },
        onHover: () => setHoveredState(code),
        onLeave: () => setHoveredState(null),
        onClick: () => handleStateClick(code)
      };
    });
    return result;
  }, [
    carrierPartnersMap,
    hoveredState,
    hoverGroup,
    selectedStateCode,
    pulsingStates,
    hasActiveFilters,
    handleStateClick
  ]);

  return (
    <div className='show-scrollbar flex min-h-0 w-full flex-1 flex-col gap-2 rounded-xl bg-white p-4'>
      <div className='flex min-h-0 w-full flex-1 flex-col'>
        <div className='relative flex min-h-0 min-w-0 w-full flex-1 flex-col'>
          {/* Legend — keep it in normal flow so it never overlays the SVG map */}
          <div className='flex shrink-0 flex-row flex-wrap items-center justify-between gap-x-4 gap-y-2 pb-2 text-sm'>
            {showTypeFilter && (
              <div className='flex w-full min-w-0 flex-col gap-1 sm:w-80'>
                <span className='text-sm font-medium text-grey'>
                  Carrier types
                </span>
                <FilterAutocomplete
                  options={carrierTypeOptions}
                  value={selectedTypeOptions}
                  onChange={next =>
                    onCarrierTypesChange(next.map(o => o.value))
                  }
                  placeholder='All carrier types'
                  getOptionLabel={o => o.label}
                  isOptionEqualToValue={(a, b) => a.value === b.value}
                  displayMode='chips'
                />
              </div>
            )}
            <div className='ml-auto flex flex-row flex-wrap items-center gap-x-4 gap-y-1'>
              <button
                type='button'
                className='flex cursor-pointer items-center border-none bg-transparent p-0 text-left'
                onMouseEnter={() => setHoverGroup('SBE')}
                onMouseLeave={() => setHoverGroup(null)}
              >
                <span
                  className='mr-2 inline-block h-3 w-3 shrink-0 rounded-full'
                  style={{ backgroundColor: SBE_FILL }}
                />
                <span>SBE</span>
              </button>
              <button
                type='button'
                className='flex cursor-pointer items-center border-none bg-transparent p-0 text-left'
                onMouseEnter={() => setHoverGroup('FFM')}
                onMouseLeave={() => setHoverGroup(null)}
              >
                <span
                  className='mr-2 inline-block h-3 w-3 shrink-0 rounded-full'
                  style={{ backgroundColor: FFM_FILL }}
                />
                <span>FFM</span>
              </button>
              {hasEmptyStates && (
                <div className='flex items-center'>
                  <span
                    className='mr-2 inline-block h-3 w-3 shrink-0 rounded-full'
                    style={{ backgroundColor: EMPTY_FILL }}
                  />
                  <span>No carriers</span>
                </div>
              )}
            </div>
          </div>
          <div className='min-h-0 flex-1 overflow-x-auto overflow-y-auto show-scrollbar z-10'>
            <div className='flex h-full min-w-[max(100%,680px)] justify-center'>
              <div className='box-border h-full w-full min-w-[680px] max-w-5xl'>
                <div className='relative h-full w-full min-h-[240px] lg:min-h-[480px]'>
                  <USAMap
                    // no tailwind preflight, so an inline svg leaves a descender gap that overflows by ~4px
                    className='block'
                    mapSettings={{
                      height: '100%'
                    }}
                    customStates={customStates}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

CarrierAppointmentsByStateMap.propTypes = {
  carrierPartnersMap: propTypes.object,
  onStateClick: propTypes.func,
  selectedStateCode: propTypes.string,
  carrierTypeOptions: propTypes.arrayOf(
    propTypes.shape({
      value: propTypes.string.isRequired,
      label: propTypes.string.isRequired
    })
  ),
  selectedCarrierTypes: propTypes.arrayOf(propTypes.string),
  onCarrierTypesChange: propTypes.func
};
